/**
 * PrintWorx3D Rewards — referral landing capture.
 *
 * On a page with ?ref=CODE: ask the Rewards service (signed App Proxy) whether the code is real. Only a code the
 * server confirms is remembered — in a first-party cookie and as the cart attribute "_pw_ref" — so it reaches the
 * order. On later pages the cookie re-applies the attribute to a fresh cart. Nothing here awards points, and
 * every failure is silent: the store never waits on Rewards.
 */
const script = document.querySelector("script[data-pw-referral]");

if (script) {
  const endpoint = (script.dataset.endpoint || "/apps/rewards").replace(/\/+$/, "");
  const param = script.dataset.param || "ref";
  const cookieDays = Math.max(1, Number(script.dataset.cookieDays || 30));
  const COOKIE = "pw_ref";
  const ATTRIBUTE = "_pw_ref";
  const CODE_RE = /^[A-Z2-9]{6,16}$/;
  const TIMEOUT_MS = 4000;

  const readCookie = () => (document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`)) || [])[1] || "";
  const writeCookie = (code) => {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE}=${encodeURIComponent(code)}; Max-Age=${cookieDays * 86400}; Path=/; SameSite=Lax${secure}`;
  };

  async function withTimeout(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      return await fetch(url, { ...options, signal: controller.signal, credentials: "same-origin" });
    } finally {
      clearTimeout(timer);
    }
  }

  async function validate(code) {
    try {
      const res = await withTimeout(`${endpoint}/referral/capture`, { method: "POST", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify({ code }) });
      const data = await res.json().catch(() => null);
      return res.status === 200 && data && data.ok && typeof data.code === "string" ? data.code : "";
    } catch {
      return "";
    }
  }

  async function applyToCart(code) {
    try {
      const cart = await (await withTimeout("/cart.js", { headers: { accept: "application/json" } })).json();
      if (cart && cart.attributes && cart.attributes[ATTRIBUTE] === code) return;
      await withTimeout("/cart/update.js", { method: "POST", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify({ attributes: { [ATTRIBUTE]: code } }) });
    } catch {
      /* the cookie keeps the code; the next page load tries again */
    }
  }

  // Respect the store's privacy settings: when Shopify's customer-privacy API says this visitor may not be tracked
  // (declined consent in a region that requires it), remember nothing. Absent API or no restriction = allowed.
  const trackingAllowed = () => {
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.userCanBeTracked === "function") return cp.userCanBeTracked() !== false;
    } catch { /* fall through */ }
    return true;
  };

  (async () => {
    if (!trackingAllowed()) return;
    const url = new URL(location.href);
    const fromUrl = (url.searchParams.get(param) || "").trim().toUpperCase();
    if (fromUrl && CODE_RE.test(fromUrl)) {
      const confirmed = await validate(fromUrl);
      if (confirmed) {
        writeCookie(confirmed);
        await applyToCart(confirmed);
      }
      url.searchParams.delete(param);
      history.replaceState(history.state, "", url.pathname + (url.search || "") + url.hash);
      return;
    }
    const remembered = decodeURIComponent(readCookie() || "").toUpperCase();
    if (remembered && CODE_RE.test(remembered)) await applyToCart(remembered);
  })();
}
